import { useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useBoolean, useSetState } from 'react-use'
import { createBrandKit } from '@/services/brand-kit'
import { hexFromArgb, themeFromImage } from '@material/material-color-utilities'

import { Field, Label, ModalGrid, SelectDropdown, SimpleInput, UploadInput } from '@makyoapp/makyo-ui'

import { queryClient } from '@/lib/react-query'
import { queryKeys } from '@/lib/react-query/query-keys'
import { appConfig } from '@/configs'
import { fromSdkBrandKit } from '@/adapters/__project__/BrandKitAdapter'
import { useBrandKitContext } from '@/context/BrandKitContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useReqError } from '@/hooks/useReqError'
import { BASE_PALETTE_COLOR, Theme } from '@/shared/brand-kit'
import { ORG_BRAND_KIT_ROUTE, ORG_ROUTE } from '@/shared/constants'
import { useFormEnter } from '@/shared/makyo-hooks'
import { cleanUpString } from '@/shared/utils'
import { BrandColorOption } from '@/components/BrandColorOption'
import { ColorStrip } from '@/components/ColorStrip'

interface NewBrandKitModalProps {
  show: boolean
  setShow: (show: boolean) => void
}

export default function NewBrandKitModal({ show, setShow }: NewBrandKitModalProps) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useBoolean(false)
  const { currentOrg } = useCurrentOrgContext()
  const [step, setStep] = useState(0)
  const { brandKits, refetchList } = useBrandKitContext()

  const [error, setError] = useState('')
  const [brandKitSetting, setBrandKitSetting] = useSetState<{
    name: string
    color: string
    image: {
      src: string
      file?: File
    }
  }>({
    name: '',
    color: BASE_PALETTE_COLOR[0].color,
    image: {
      src: '',
      file: undefined,
    },
  })

  const imgRef = useRef(null)
  const [brandKit, setBrandKit] = useState<Theme | null>(null)
  const navigate = useNavigate()

  const printAPIError = useReqError()

  useEffect(() => {
    if (step > 0 && imgRef.current) {
      themeFromImage(imgRef.current)
        .then((res) => {
          setBrandKit(res as unknown as Theme)
          setBrandKitSetting({
            color: hexFromArgb(res.source),
          })
        })
        .catch(() => {
          printAPIError({
            status: 302,
            message: 'error reading image to brand kit theme',
          })
        })
    }
  }, [step, setBrandKit, setBrandKitSetting, brandKitSetting.image, imgRef, printAPIError])

  const onHide = useCallback(() => {
    setShow(false)
    setStep(0)
    setBrandKit(null)
    setError('')
    setBrandKitSetting({
      name: '',
      color: BASE_PALETTE_COLOR[0].color,
      image: {
        src: '',
        file: undefined,
      },
    })
  }, [setShow, setStep, setBrandKit, setBrandKitSetting])

  const handleSubmit = useCallback(() => {
    if (currentOrg) {
      const split = brandKitSetting.image?.file?.name?.split('.')
      const ext = split?.[split.length - 1]
      setIsLoading(true)

      createBrandKit({
        orgId: currentOrg.id,
        name: brandKitSetting.name,
        logo: step === 1 ? brandKitSetting.image.file : null,
        logoExt: step === 1 ? ext : '',
        color: brandKitSetting.color,
      })
        .then((res) => {
          const bk = fromSdkBrandKit(res)
          refetchList()
          navigate(`${ORG_ROUTE}/${currentOrg.id}/${ORG_BRAND_KIT_ROUTE}/${bk.id}`)
        })
        .catch(printAPIError)
        .finally(() => {
          setIsLoading(false)
          onHide()
          queryClient.invalidateQueries({ queryKey: [queryKeys.orgBrandKitsV2, currentOrg.id] })
        })
    }
  }, [
    currentOrg,
    brandKitSetting.image.file,
    brandKitSetting.name,
    brandKitSetting.color,
    step,
    setIsLoading,
    printAPIError,
    refetchList,
    navigate,
    onHide,
  ])

  const handleAddNew = useCallback(() => {
    if (brandKitSetting.name) {
      if (step < 1) {
        setStep((prev) => prev + 1)
      } else {
        handleSubmit()
      }
    }
  }, [step, setStep, brandKitSetting.name, handleSubmit])

  useFormEnter('new-brand-kit', handleAddNew)

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (!!brandKitSetting.name) {
      timeout = setTimeout(() => {
        setBrandKitSetting((prev) => ({
          ...prev,
          name: cleanUpString(brandKitSetting.name),
        }))
      }, 1000)
    }

    return () => {
      clearTimeout(timeout)
    }
  }, [brandKitSetting.name, setBrandKitSetting])

  const nameCharCount = brandKitSetting.name.trim().length
  const validNameLength =
    nameCharCount >= appConfig.charConstraints.min && nameCharCount < appConfig.charConstraints.max

  const stepOneButtonDisabled = !validNameLength

  return (
    <ModalGrid
      show={show}
      onHide={onHide}
      title={t('Add New Brand Kits')}
      onConfirm={handleAddNew}
      onCancel={step < 1 ? onHide : () => setStep((prev) => prev - 1)}
      primaryTrailing={t(step < 1 ? 'Next' : 'Create')}
      disabled={Boolean(error) || (step === 0 && stepOneButtonDisabled) || (step === 1 && !brandKitSetting.image.file)}
      isLoading={isLoading}
      leadingTitle={step > 0 ? t('Back') : t('Cancel')}
      prefixSubmitComponent={
        step === 1 && (
          <button
            type='button'
            className='mr-2'
            onClick={() => {
              setStep(2)
            }}
          >
            <Trans>Skip</Trans>
          </button>
        )
      }
    >
      <form
        id='new-brand-kit'
        onSubmit={(e) => {
          e.preventDefault()
          handleAddNew()
        }}
      >
        {step === 0 && (
          <Field>
            <Label>
              <Trans>Name</Trans>
            </Label>
            <SimpleInput
              required={true}
              value={brandKitSetting.name}
              error={error}
              onChange={(e) => {
                const value = e.currentTarget.value
                const exist = brandKits.findIndex((i) => i.name === value)
                if (exist !== -1) {
                  setError(t('Brand kit already exists!'))
                } else {
                  setError('')
                }
                setBrandKitSetting({
                  name: value,
                })
              }}
              type='text'
              withCounterConfig={appConfig.charConstraints}
            />
          </Field>
        )}
        {step === 1 && (
          <>
            <div className='text-light'>
              <Trans>The logo will be used to create a brand kit for you.</Trans>
            </div>
            <Field isVertical>
              <UploadInput
                previewLabel={brandKitSetting.image.file?.name}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) {
                    setBrandKitSetting({
                      image: {
                        src: URL.createObjectURL(f),
                        file: f,
                      },
                    })
                  }
                }}
              />
              {brandKitSetting.image.src && (
                <div className='flex-c4 flex-col'>
                  <img alt='' ref={imgRef} src={brandKitSetting.image.src} className='max-w-[40%]' />
                  {/*brandKit && <Preview isVertical theme={brandKit} />*/}
                  {brandKit && (
                    <div className='w-full'>
                      <div>
                        <Trans>Generated Colors</Trans>
                      </div>
                      <ColorStrip theme={brandKit} />
                    </div>
                  )}
                </div>
              )}
            </Field>
          </>
        )}
        {step === 2 && (
          <>
            <div className='mb-2'>
              <Trans>Select color palette to create your brand kit</Trans>
            </div>
            <div>
              <SelectDropdown
                arrow={false}
                toggleClassName='palette-dd'
                value={BASE_PALETTE_COLOR.find((c) => c.color === brandKitSetting.color)}
                onChange={(opt, e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setBrandKitSetting({
                    color: opt.color,
                  })
                }}
                options={BASE_PALETTE_COLOR}
                optionLabel={(opt) => opt.name}
                renderOption={(opt, inToggle) => {
                  return <BrandColorOption opt={opt} inToggle={inToggle} />
                }}
              />
            </div>
          </>
        )}
      </form>
    </ModalGrid>
  )
}
